import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AuthCallback from './components/auth/AuthCallback';
import PasswordReset from './components/auth/PasswordReset';
import { CoolClimateNavigation } from './components/layout/CoolClimateNavigation';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { CoolClimateCalculator } from './components/pages/CoolClimateCalculator';
import { Energy } from './components/pages/Energy';
import { GHGInventory } from './components/pages/GHGInventory';
import MapsAnalysis from './components/pages/MapsAnalysis';
import { Other } from './components/pages/Other';
import { Overview } from './components/pages/Overview';
import { People } from './components/pages/People';
import { Community } from './components/pages/Community';
import SimpleDevelopersLogPage from './components/pages/SimpleDevelopersLogPage';
import { Transportation } from './components/pages/Transportation';
import UserManagementPage from './components/pages/UserManagementPage';
import DeveloperPage from './components/pages/DeveloperPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider, useLocation as useLocationHook } from './contexts/LocationContext';
import { UserRoleProvider } from './contexts/UserRoleContext';


function AppContent() {
  const { selectedLocation, selectLocation } = useLocationHook();
  const { loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Allow unauthenticated access - no login requirement

  // Check if we're on the Overview page
  const isOverviewPage = location.pathname === '/';
  
  // Check if we're on the CoolClimate Calculator page
  const isCoolClimatePage = location.pathname === '/coolclimate';

  const handleLocationSelect = (selectedLocationName: string) => {
    selectLocation(selectedLocationName);
    // Only navigate to Transportation if coming from Overview page
    if (location.pathname === '/') {
      navigate('/transportation');
      // Scroll to top when navigating from Overview to Transportation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Otherwise, stay on current page - location change will update data
    // Do not scroll to top when changing location on other pages - maintain user's scroll position
  };

  const handleNavigateToOverview = () => {
    navigate('/');
    // Scroll to top when navigating to overview
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionChange = (section: string) => {
    // Handle section change
    if (section === 'people') {
      navigate('/people');
    } else if (section === 'overview') {
      navigate('/');
    } else if (section === 'ghg') {
      navigate('/ghg');
    } else if (section === 'buildings') {
      navigate('/buildings');
    } else if (section === 'transportation') {
      navigate('/transportation');
    } else if (section === 'maps') {
      navigate('/maps');
    } else if (section === 'community') {
      navigate('/community');
    } else if (section === 'other') {
      navigate('/other');
    }
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Header 
        onNavigateToOverview={handleNavigateToOverview}
        onLocationSelect={handleLocationSelect}
        currentLocation={selectedLocation}
      />
      {/* Show appropriate navigation based on page */}
      {!isOverviewPage && !isCoolClimatePage && <Navigation onSectionChange={handleSectionChange} />}
      {isCoolClimatePage && <CoolClimateNavigation onSectionChange={handleSectionChange} />}
      
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <Overview onLocationSelect={handleLocationSelect} />
            } 
          />
          <Route 
            path="/people" 
            element={
              <People location={selectedLocation} />
            } 
          />
          <Route 
            path="/ghg" 
            element={
              <GHGInventory location={selectedLocation} />
            } 
          />
          <Route 
            path="/buildings" 
            element={
              <Energy location={selectedLocation} />
            } 
          />
          <Route 
            path="/transportation" 
            element={
              <Transportation location={selectedLocation} />
            } 
          />
          <Route
            path="/maps"
            element={
              <MapsAnalysis />
            }
          />
          <Route
            path="/community"
            element={
              <Community />
            }
          />
          <Route
            path="/other"
            element={
              <Other />
            }
          />
          <Route 
            path="/coolclimate" 
            element={
              <CoolClimateCalculator />
            } 
          />
                      <Route 
                        path="/developers-log" 
                        element={
                          <SimpleDevelopersLogPage />
                        } 
                      />
          <Route 
            path="/user-management" 
            element={
              <UserManagementPage />
            } 
          />
          <Route 
            path="/developer/:developerSlug" 
            element={
              <DeveloperPage />
            } 
          />
          <Route 
            path="/auth/callback" 
            element={
              <AuthCallback />
            } 
          />
          <Route 
            path="/auth/reset-password" 
            element={
              <PasswordReset />
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      
      {/* Footer - Appears on all pages */}
      <Footer />
    </div>
  );
}

function AppWithUserRole() {
  const { user } = useAuth();
  
  // Always render AppContent, with or without user
  // UserRoleProvider will handle the case where userId is null/undefined
  return (
    <UserRoleProvider userId={user?.id}>
      <AppContent />
    </UserRoleProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <Router>
          <AppWithUserRole />
        </Router>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;